import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import DownloadIcon from '@mui/icons-material/Download'
import LogoutIcon from '@mui/icons-material/Logout'
import ReplayIcon from '@mui/icons-material/Replay'
import SaveIcon from '@mui/icons-material/Save'
import { LoadingButton } from '@mui/lab'
import { Box, Button, IconButton, useMediaQuery } from '@mui/material'
import React, { useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { Auth } from '../api/Auth'
import { Download } from '../api/Download'
import { useActions } from '../hooks/useActions'
import { theme } from '../theme'
import Context from '../utils/context/Context'
import { SessionContext } from './SessionTimeout'

function SaveButton({ isMobile, setLoading, loading }) {
  const buttonLabel = 'save'

  if (isMobile) {
    return (
      <IconButton
        onClick={() => setLoading(buttonLabel)}
        disabled={!!loading?.save}
        sx={{ width: 45, height: 45 }}
      >
        <SaveIcon />
      </IconButton>
    )
  }

  return (
    <LoadingButton
      startIcon={<SaveIcon />}
      onClick={() => setLoading(buttonLabel)}
      loading={!!loading?.[buttonLabel]}
      variant="outlined"
    >
      <span>Save</span>
    </LoadingButton>
  )
}

function LogoutButton({ isMobile, addToast, setLoading, loading }) {
  const history = useHistory()

  if (isMobile) {
    return (
      <IconButton
        onClick={handleLogout}
        disabled={!!loading?.save}
        color="error"
        sx={{ width: 45, height: 45, backgroundColor: (theme) => theme.palette.error.bg }}
      >
        <LogoutIcon />
      </IconButton>
    )
  }

  return (
    <LoadingButton
      startIcon={<LogoutIcon />}
      variant="outlined"
      color={'grey'}
      loading={!!loading?.save}
      onClick={handleLogout}
    >
      Logout
    </LoadingButton>
  )

  async function handleLogout() {
    await setLoading('logout')
    await Auth.logout({ addToast })

    history.push('/user-logout', {
      message: 'You have logged out. Please login again to restart the questionnaire.',
    })
  }
}

function PdfButton({ addToast, setLoading, loading }) {
  const buttonLabel = 'pdf'

  return (
    <Button
      startIcon={<DownloadIcon />}
      onClick={async () => {
        setLoading(buttonLabel)
        await Download.pdfDownload({ addToast })
      }}
      disabled={loading ? !!loading[buttonLabel] : false}
    >
      Download PDF
    </Button>
  )
}

function ResetButton({ addToast, loadingControls, loading }) {
  const buttonLabel = 'reset'
  const { resetState } = useActions()

  return (
    <LoadingButton
      color="error"
      startIcon={<ReplayIcon />}
      disabled={!!loading?.reset}
      loading={!!loading?.save}
      variant="outlined"
      onClick={async () => {
        loadingControls(buttonLabel, true)
        await resetState()
        loadingControls(buttonLabel, false)
        addToast(
          'reset',
          'success',
          'Form Responses Reset. You must manually remove patients from the ' +
            'PhenoTips dashboard.',
        )
      }}
    >
      Reset
    </LoadingButton>
  )
}

function NavigateButton({ isMobile, label, icon, handleMove, loadingControls, disabled, back }) {
  if (isMobile) {
    return (
      <IconButton
        onClick={handleClick}
        disabled={disabled}
        sx={{ width: 45, height: 45 }}
        aria-label={label}
      >
        {icon}
      </IconButton>
    )
  }

  return (
    <LoadingButton
      variant={back ? 'outlined' : 'contained'}
      onClick={handleClick}
      disabled={disabled}
      startIcon={back && icon}
      endIcon={!back && icon}
      aria-label={label}
    >
      {label}
    </LoadingButton>
  )

  async function handleClick() {
    loadingControls('save', true)
    await handleMove()
    loadingControls('save', false)
  }
}

function Controls({
  demoMode,
  handleMovePrevious,
  canMovePrevious,
  isLastStep,
  saveAndMoveToNext,
  setSubmitWarning,
  btnLabels,
  loadingControls,
}) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))
  const { loading, addToast } = useContext(Context)
  const { activate } = useContext(SessionContext)
  const { sendStateData } = useActions()

  const saveThen = async () => {
    await sendStateData()
    addToast('save', 'success', 'Save completed')
    activate()

    return true
  }
  const setLoading = async (label) => {
    loadingControls(label, true)
    await saveThen()
    loadingControls(label, false)
  }

  return (
    <Box
      sx={{
        backdropFilter: 'blur(10px)',
        bgColor: 'white.transparent',
        boxShadow: 2,
        bottom: '0',
        display: 'flex',
        gap: 2,
        position: 'sticky',
        py: 3,
        px: 10,
        zIndex: '1',
        [theme.breakpoints.down('md')]: {
          position: 'fixed',
          py: 2,
          px: 2,
          width: '100%',
        },
      }}
      component="footer"
      aria-label="navigation controls"
    >
      <SaveButton
        isMobile={isMobile}
        saveThen={saveThen}
        setLoading={setLoading}
        loading={loading}
      />
      <LogoutButton
        isMobile={isMobile}
        addToast={addToast}
        setLoading={setLoading}
        loading={loading}
      />
      {demoMode && !isMobile && (
        <>
          <PdfButton addToast={addToast} setLoading={setLoading} loading={loading} />
          <ResetButton addToast={addToast} loadingControls={loadingControls} loading={loading} />
        </>
      )}
      <Box flexGrow={1}></Box>
      <NavigateButton
        isMobile={isMobile}
        label={btnLabels[0]}
        icon={<ArrowBackIcon />}
        handleMove={handleMovePrevious}
        loadingControls={loadingControls}
        disabled={!canMovePrevious() || loading?.save}
        back
      />
      {!isLastStep() ? (
        <NavigateButton
          isMobile={isMobile}
          label={btnLabels[1]}
          icon={<ArrowForwardIcon />}
          handleMove={saveAndMoveToNext}
          loadingControls={loadingControls}
          disabled={!!loading?.save}
        />
      ) : (
        <LoadingButton
          onClick={() => setSubmitWarning(true)}
          loading={!!loading?.save}
          endIcon={<ArrowForwardIcon />}
          variant="contained"
        >
          {isMobile ? 'Submit' : 'Submit questionnaire'}
        </LoadingButton>
      )}
    </Box>
  )
}

export default Controls
